import * as React from 'react';
import { parseThemePreference, resolveTheme, type ThemePreference } from './theme-preference';

type ThemeContextValue = {
  theme: ThemePreference;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: ThemePreference) => void;
};

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

type ThemeProviderProps = {
  children: React.ReactNode;
  storageKey: string;
  defaultTheme?: ThemePreference;
};

export function ThemeProvider({
  children,
  storageKey,
  defaultTheme = 'system',
}: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<ThemePreference>(() => {
    if (typeof window === 'undefined') {
      return defaultTheme;
    }
    const stored = parseThemePreference(window.localStorage.getItem(storageKey));
    return stored ?? defaultTheme;
  });

  const setTheme = React.useCallback(
    (newTheme: ThemePreference) => {
      setThemeState(newTheme);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, newTheme);
      }
    },
    [storageKey],
  );

  const systemIsDark = React.useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  const resolvedTheme = resolveTheme(theme, systemIsDark);

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
  }, [resolvedTheme]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (theme !== 'system') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      document.documentElement.classList.toggle('dark', media.matches);
    };
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [theme]);

  const value = React.useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
